import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, ArrowLeft, CalendarCheck } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL || "https://calendly.com/fgallibusiness/30min";

const focusOptions = [
  { value: "business", title: "Business Coaching", desc: "Leadership, decisioni, crescita" },
  { value: "loneliness", title: "Loneliness Coaching", desc: "Solitudine al vertice, presenza" },
  { value: "sport", title: "Sport Coaching", desc: "Mentalità, pressione, performance" },
  { value: "both", title: "Entrambi", desc: "La persona e l'impresa insieme" },
];

const urgencyOptions = [
  { value: "exploring", label: "Sto esplorando" },
  { value: "soon", label: "Voglio iniziare a breve" },
  { value: "now", label: "Voglio iniziare subito" },
];

const budgetOptions = [
  { value: "under_1500", label: "Meno di 1.500 €", hint: "Non pronto a investire ora" },
  { value: "1500_3000", label: "1.500 € – 3.000 €", hint: "" },
  { value: "3000_6000", label: "3.000 € – 6.000 €", hint: "" },
  { value: "over_6000", label: "Oltre 6.000 €", hint: "" },
];

const initial = {
  focus: "",
  urgency: "",
  budget: "",
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  challenge: "",
  motivation: "",
  consent: false,
  website: "",
};

const stepVariants = {
  enter: { opacity: 0, x: 30 },
  center: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 },
};

export default function QualificationForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const canProceed = () => {
    if (step === 1) return data.focus && data.urgency;
    if (step === 2) return data.budget;
    if (step === 3)
      return (
        data.first_name.trim() &&
        data.last_name.trim() &&
        /\S+@\S+\.\S+/.test(data.email) &&
        data.phone.trim() &&
        data.challenge.trim().length > 10 &&
        data.motivation.trim().length > 10 &&
        data.consent
      );
    return false;
  };

  const next = () => {
    if (!canProceed()) {
      toast.error("Completa tutti i campi per proseguire.");
      return;
    }
    setStep((s) => Math.min(3, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canProceed()) {
      toast.error("Completa tutti i campi per inviare la candidatura.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/leads`, data);
      setResult(res.data);
      window.scrollTo && document.getElementById("candidatura")?.scrollIntoView({ block: "start" });
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || "Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-foreground placeholder:text-muted-foreground/60 focus:border-primary focus:outline-none transition-colors duration-300";

  return (
    <section
      id="candidatura"
      className="relative bg-background py-28 md:py-40 border-t border-white/10"
      data-testid="qualification-section"
    >
      <div className="max-w-[1000px] mx-auto px-6 md:px-10">
        <div className="mb-14 md:mb-20 text-center max-w-2xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-6">
            <span className="h-px w-10 bg-primary" />
            <span className="text-[11px] tracking-[0.35em] text-primary">
              CANDIDATURA
            </span>
            <span className="h-px w-10 bg-primary" />
          </div>
          <h2 className="font-serif font-light text-4xl md:text-5xl text-foreground leading-tight">
            Prenota la tua call conoscitiva gratuita.
          </h2>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            Questo non è un semplice contatto: è una candidatura. Rispondi con
            onestà — le tue risposte mi aiutano a capire se posso davvero
            esserti utile.
          </p>
        </div>

        <div className="rounded-2xl border border-white/10 bg-card p-6 md:p-12">
          <AnimatePresence mode="wait">
            {result ? (
              <ResultScreen key="result" result={result} data={data} />
            ) : (
              <div key="form">
                {/* Progress */}
                <div className="flex items-center gap-3 mb-10" data-testid="form-progress">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex-1">
                      <div
                        className={`h-0.5 rounded-full transition-colors duration-500 ${
                          s <= step ? "bg-primary" : "bg-white/10"
                        }`}
                      />
                      <span
                        className={`mt-2 block text-[10px] tracking-[0.2em] ${
                          s <= step ? "text-primary" : "text-muted-foreground"
                        }`}
                      >
                        0{s}
                      </span>
                    </div>
                  ))}
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    variants={stepVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {step === 1 && (
                      <div data-testid="form-step-1">
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-8">
                          Su cosa vuoi lavorare?
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
                          {focusOptions.map((o) => (
                            <button
                              key={o.value}
                              onClick={() => update("focus", o.value)}
                              className={`text-left rounded-xl border p-5 transition-colors duration-300 ${
                                data.focus === o.value
                                  ? "border-primary bg-primary/5"
                                  : "border-white/10 hover:border-white/30"
                              }`}
                              data-testid={`focus-${o.value}`}
                            >
                              <div className="text-foreground font-medium mb-1">
                                {o.title}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {o.desc}
                              </div>
                            </button>
                          ))}
                        </div>

                        <div className="text-sm text-muted-foreground mb-4">
                          Quanto è urgente per te?
                        </div>
                        <div className="flex flex-wrap gap-3">
                          {urgencyOptions.map((o) => (
                            <button
                              key={o.value}
                              onClick={() => update("urgency", o.value)}
                              className={`rounded-full border px-5 py-2.5 text-sm transition-colors duration-300 ${
                                data.urgency === o.value
                                  ? "border-primary text-primary bg-primary/5"
                                  : "border-white/15 text-foreground/80 hover:border-white/40"
                              }`}
                              data-testid={`urgency-${o.value}`}
                            >
                              {o.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 2 && (
                      <div data-testid="form-step-2">
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-3">
                          Sei pronto a investire su di te?
                        </h3>
                        <p className="text-sm text-muted-foreground mb-8 max-w-xl">
                          Un percorso serio richiede un investimento reale. Sii
                          onesto: mi aiuta a proporti solo ciò che ha senso per te.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {budgetOptions.map((o) => (
                            <button
                              key={o.value}
                              onClick={() => update("budget", o.value)}
                              className={`text-left rounded-xl border p-5 transition-colors duration-300 ${
                                data.budget === o.value
                                  ? "border-primary bg-primary/5"
                                  : "border-white/10 hover:border-white/30"
                              }`}
                              data-testid={`budget-${o.value}`}
                            >
                              <div className="text-foreground font-medium">
                                {o.label}
                              </div>
                              {o.hint && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  {o.hint}
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    {step === 3 && (
                      <div data-testid="form-step-3">
                        <h3 className="font-serif text-2xl md:text-3xl text-foreground mb-8">
                          Raccontami di te.
                        </h3>
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          value={data.website}
                          onChange={(e) => update("website", e.target.value)}
                          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                          data-testid="hp-website"
                        />
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                          <input
                            className={inputCls}
                            placeholder="Nome"
                            value={data.first_name}
                            onChange={(e) => update("first_name", e.target.value)}
                            data-testid="input-first-name"
                          />
                          <input
                            className={inputCls}
                            placeholder="Cognome"
                            value={data.last_name}
                            onChange={(e) => update("last_name", e.target.value)}
                            data-testid="input-last-name"
                          />
                          <input
                            className={inputCls}
                            type="email"
                            placeholder="Email"
                            value={data.email}
                            onChange={(e) => update("email", e.target.value)}
                            data-testid="input-email"
                          />
                          <input
                            className={inputCls}
                            placeholder="Telefono"
                            value={data.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            data-testid="input-phone"
                          />
                        </div>
                        <div className="mb-6">
                          <label className="text-sm text-muted-foreground">
                            Qual è la sfida che stai affrontando ora?
                          </label>
                          <textarea
                            className={`${inputCls} resize-none mt-2`}
                            rows={3}
                            placeholder="Scrivi liberamente..."
                            value={data.challenge}
                            onChange={(e) => update("challenge", e.target.value)}
                            data-testid="input-challenge"
                          />
                        </div>
                        <div>
                          <label className="text-sm text-muted-foreground">
                            Perché proprio adesso? Cosa ti spinge a candidarti?
                          </label>
                          <textarea
                            className={`${inputCls} resize-none mt-2`}
                            rows={3}
                            placeholder="La tua motivazione..."
                            value={data.motivation}
                            onChange={(e) => update("motivation", e.target.value)}
                            data-testid="input-motivation"
                          />
                        </div>

                        <div className="mt-8 flex items-start gap-3">
                          <button
                            type="button"
                            onClick={() => update("consent", !data.consent)}
                            aria-pressed={data.consent}
                            data-testid="input-consent"
                            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors duration-300 ${
                              data.consent
                                ? "border-primary bg-primary text-primary-foreground"
                                : "border-white/30 hover:border-white/60"
                            }`}
                          >
                            {data.consent && <Check size={14} strokeWidth={3} />}
                          </button>
                          <span className="text-xs text-muted-foreground leading-relaxed max-w-xl">
                            <span
                              onClick={() => update("consent", !data.consent)}
                              className="cursor-pointer"
                            >
                              Ho letto e accetto l'{" "}
                            </span>
                            <a
                              href="/privacy"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary underline underline-offset-2 hover:text-primary/80"
                              data-testid="consent-privacy-link"
                            >
                              informativa sulla privacy
                            </a>{" "}
                            <span
                              onClick={() => update("consent", !data.consent)}
                              className="cursor-pointer"
                            >
                              e autorizzo il trattamento dei miei dati personali
                              per essere ricontattato (Reg. UE 2016/679 - GDPR).
                            </span>
                          </span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Nav */}
                <div className="mt-12 flex items-center justify-between">
                  <button
                    onClick={back}
                    disabled={step === 1}
                    className={`inline-flex items-center gap-2 text-sm tracking-[0.1em] transition-colors duration-300 ${
                      step === 1
                        ? "opacity-0 pointer-events-none"
                        : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="form-back"
                  >
                    <ArrowLeft size={16} /> INDIETRO
                  </button>

                  {step < 3 ? (
                    <button
                      onClick={next}
                      className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm tracking-[0.12em] hover:bg-primary/90 transition-colors duration-300"
                      data-testid="form-next"
                    >
                      CONTINUA →
                    </button>
                  ) : (
                    <button
                      onClick={submit}
                      disabled={submitting}
                      className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-3.5 text-sm tracking-[0.12em] hover:bg-primary/90 transition-colors duration-300 disabled:opacity-60"
                      data-testid="form-submit"
                    >
                      {submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" /> INVIO...
                        </>
                      ) : (
                        "INVIA CANDIDATURA →"
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>

        <div className="mt-16 max-w-3xl mx-auto" data-testid="form-faq">
          <h3 className="font-serif text-2xl md:text-3xl text-foreground text-center mb-8">
            Prima di candidarti
          </h3>
          <Accordion type="single" collapsible className="w-full">
            {[
              {
                q: "È davvero una call gratuita?",
                a: "Sì: 30 minuti senza alcun costo né impegno. Non è una call di vendita, ma una conversazione onesta per capire se possiamo lavorare bene insieme.",
              },
              {
                q: "Cosa succede dopo l'invio del form?",
                a: "Leggo personalmente ogni candidatura. Se c'è allineamento ti invito a prenotare la call conoscitiva; in caso contrario ti rispondo comunque, con trasparenza.",
              },
              {
                q: "Perché mi chiedi il budget?",
                a: "Per rispetto del tuo tempo e del mio. Un percorso serio richiede un investimento reale: conoscerlo mi evita di proporti qualcosa che non è nelle tue possibilità in questo momento.",
              },
              {
                q: "I miei dati sono al sicuro?",
                a: "Assolutamente. Riservatezza totale: i dati sono trattati secondo il GDPR (trovi tutto nell'informativa sulla privacy) e non vengono mai condivisi con terzi.",
              },
              {
                q: "Le sessioni sono online o in presenza?",
                a: "Entrambe: online ovunque tu sia, oppure in presenza su appuntamento a Milano, Firenze, Roma, Dubai, Londra e New York.",
              },
            ].map((item, i) => (
              <AccordionItem
                key={i}
                value={`q-${i}`}
                className="border-white/10"
                data-testid={`form-faq-${i}`}
              >
                <AccordionTrigger className="text-left text-foreground hover:text-primary hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ResultScreen({ result, data }) {
  if (result.qualified) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
        data-testid="result-qualified"
      >
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Check size={26} />
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
          Grazie, {data.first_name}. Candidatura ricevuta.
        </h3>
        <p className="text-muted-foreground max-w-xl mx-auto leading-relaxed mb-10">
          Il tuo profilo è in linea con i percorsi che seguo. Scegli qui sotto
          l'orario per la tua call conoscitiva gratuita di 30 minuti.
        </p>

        {CALENDLY_URL ? (
          <div className="rounded-xl overflow-hidden border border-white/10">
            <iframe
              title="Prenota call"
              src={CALENDLY_URL}
              className="w-full"
              style={{ height: 680, background: "hsl(0 0% 6%)" }}
              data-testid="calendly-embed"
            />
          </div>
        ) : (
          <a
            href={CALENDLY_URL || "#"}
            className="inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground px-8 py-4 text-sm tracking-[0.12em]"
            data-testid="calendly-link"
          >
            <CalendarCheck size={18} /> SCEGLI UN ORARIO
          </a>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="text-center max-w-xl mx-auto"
      data-testid="result-unqualified"
    >
      <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-white/5 text-primary">
        <Check size={26} />
      </div>
      <h3 className="font-serif text-3xl md:text-4xl text-foreground mb-4">
        Grazie per la tua onestà, {data.first_name}.
      </h3>
      <p className="text-muted-foreground leading-relaxed">
        Ho ricevuto la tua candidatura. In questo momento, però, il percorso che
        offro richiede un tipo di investimento che forse non è ancora quello
        giusto per te — e preferisco dirtelo con trasparenza piuttosto che
        farti perdere tempo.
      </p>
      <p className="text-muted-foreground leading-relaxed mt-4">
        Quando sentirai che è il momento giusto per investire davvero su di te,
        sarò qui. Nel frattempo, ti scriverò qualche risorsa utile via email.
      </p>
    </motion.div>
  );
}
